import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import IconButton from "../IconButton";
import { CloseCircleIcon } from "../SVGs";
import WPHeading from "../WPHeading";

import "./WPTextArea.scss";

const MAX_TEXT_COUNT = 1000;
const ERROR_TIMER = 3000;

const WPTextArea = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  rows,
  helperText,
  maxCount,
  required,
  disabled,
  onBlur,
  validator,
}) => {
  const maxCountValue = maxCount || MAX_TEXT_COUNT;
  const [isError, setIsError] = useState(false);
  const timerRef = useRef(null);

  const currentCount = value?.length || 0;

  const charCountText = useMemo(
    () => `${currentCount}/${maxCountValue}`,
    [currentCount, maxCountValue]
  );

  const clearErrorTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startErrorTimer = useCallback(() => {
    clearErrorTimer();
    timerRef.current = setTimeout(() => setIsError(false), ERROR_TIMER);
  }, [clearErrorTimer]);

  useEffect(() => () => clearErrorTimer(), [clearErrorTimer]);

  const [lastValidVal, setLastValidVal] = useState(value || "");

  const handleTextChange = useCallback(
    ({ target }) => {
      let newValue = target.value;
      if (newValue.length > maxCountValue) {
        setIsError(true);
        startErrorTimer();
        newValue = newValue.slice(0, maxCountValue);
      } else if (validator) {
        if (validator.test(newValue) || newValue === "") {
          setIsError(false);
          clearErrorTimer();
          setLastValidVal(newValue);
        } else {
          setIsError(true);
          startErrorTimer();
          newValue = lastValidVal;
        }
      } else {
        setIsError(false);
        clearErrorTimer();
        setLastValidVal(newValue);
      }
      onChange(newValue);
    },
    [
      maxCountValue,
      onChange,
      startErrorTimer,
      clearErrorTimer,
      validator,
      lastValidVal,
    ]
  );

  const handleClearText = useCallback(() => {
    onChange("");
    setIsError(false);
    clearErrorTimer();
  }, [onChange, clearErrorTimer]);

  return (
    <div className="textarea-container">
      {label && (
        <div className="text-area-label">
          {required && <span className="mandatory-icon">*</span>}
          <WPHeading
            size="small"
            type="default"
            secondary
            text={label}
            fontNoto
          />
        </div>
      )}
      <div className="text-area-btn-container">
        <textarea
          id={id}
          name={id}
          rows={rows}
          value={value}
          onBlur={onBlur}
          disabled={disabled}
          aria-invalid={!!isError}
          placeholder={placeholder}
          onChange={handleTextChange}
          className={`textarea ${isError ? "textarea--error" : ""}`}
        />
        {currentCount > 0 && !disabled && (
          <IconButton
            className="clear-icon"
            placement="bottom"
            onClick={handleClearText}
          >
            <CloseCircleIcon type={isError ? "error" : "default"} />
          </IconButton>
        )}
      </div>
      <div className="textarea-helper-container">
        {helperText && (
          <WPHeading
            size="xSmall"
            type="default"
            secondary
            text={helperText}
            fontNoto
            color="var(--du-color-grey)"
          />
        )}
        {!disabled && (
          <WPHeading
            fontNoto
            secondary
            size="xSmall"
            type="default"
            text={charCountText}
            className="count-container"
            color={
              currentCount === maxCountValue
                ? "var(--du-color-red)"
                : "var(--du-color-grey)"
            }
          />
        )}
      </div>
    </div>
  );
};

WPTextArea.propTypes = {
  id: PropTypes.string,
  rows: PropTypes.number,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  maxCount: PropTypes.number,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  validator: PropTypes.instanceOf(RegExp),
};

WPTextArea.defaultProps = {
  rows: 5,
  label: "",
  value: "",
  helperText: "",
  placeholder: "",
  required: false,
  disabled: false,
  id: "wp-text-area",
  maxCount: MAX_TEXT_COUNT,
};

export default WPTextArea;
